import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import SubHeader from '../../layout/components/SubHeader';
import { useEyeconxStore } from '../../../store/eyeconxStore';
import surveyService from '../../../services/survey.service';
import { confirmAction } from '../../../utils/ecxSwal';
import './Surveys.css';

const escapeHtml = (value = '') =>
    String(value)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');

function SelectTemplate() {
    const clinicId = useEyeconxStore((state) => state.user?.clinicId);
    const navigate = useNavigate();
    const [templates, setTemplates] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [installingId, setInstallingId] = useState(null);

    const fetchTemplates = useCallback(
        async (options = {}) => {
            const { silent = false } = options;
            if (!clinicId) return;
            if (!silent) setLoading(true);
            try {
                const res = await surveyService.getSurveyTemplates(clinicId, 0, 100, {
                    sortBy: 'title',
                    sortOrder: 'ASC',
                });
                setTemplates(res.data.content || []);
            } catch (err) {
                console.error(err);
                toast.error('Failed to load survey templates');
                setTemplates([]);
            } finally {
                if (!silent) setLoading(false);
            }
        },
        [clinicId]
    );

    useEffect(() => {
        fetchTemplates();
    }, [fetchTemplates]);

    const filtered = useMemo(() => {
        const term = searchTerm.trim().toLowerCase();
        if (!term) return templates;
        return templates.filter((t) => {
            const hay = `${t.title || ''} ${t.description || ''} ${t.category || ''}`.toLowerCase();
            return hay.includes(term);
        });
    }, [templates, searchTerm]);

    const handleRefresh = async () => {
        setIsRefreshing(true);
        try {
            await fetchTemplates({ silent: true });
        } finally {
            setIsRefreshing(false);
        }
    };

    const handleInstall = async (template) => {
        const confirm = await confirmAction({
            title: 'Install template?',
            html: `<p>"<strong>${escapeHtml(template.title || 'Untitled')}</strong>" will be copied as a draft survey.</p>`,
            iconClass: 'bi bi-download',
            confirmText: 'Install',
        });
        if (!confirm.isConfirmed) return;

        setInstallingId(template.id);
        try {
            await surveyService.installSurveyTemplate(clinicId, template.id);
            toast.success('Template installed');
            navigate('/surveys');
        } catch (err) {
            toast.error(err.response?.data?.message || 'Failed to install template');
        } finally {
            setInstallingId(null);
        }
    };

    return (
        <div className="ecx-page surveys-page ecx-page--full-width">
            <SubHeader
                title="Survey templates"
                description="Install a global template into this clinic"
                icon="bi-clipboard-check"
                containerClass="w-100 px-4"
            >
                <div className="d-flex flex-wrap gap-2 justify-content-end">
                    <button
                        type="button"
                        className="btn btn-light-primary btn-sm rounded-pill"
                        onClick={handleRefresh}
                        disabled={loading || isRefreshing}
                    >
                        <i className={`bi bi-arrow-clockwise me-1${isRefreshing ? ' surveys-spin' : ''}`} />
                        Refresh
                    </button>
                    <Link to="/surveys/create-survey" className="btn btn-sm btn-outline-primary rounded-pill">
                        <i className="bi bi-plus-circle me-1" />
                        Start blank
                    </Link>
                    <Link to="/surveys" className="btn btn-sm btn-outline-secondary rounded-pill">
                        <i className="bi bi-chevron-left me-1" />
                        Back
                    </Link>
                </div>
            </SubHeader>

            <div className="ecx-page-body">
                <div className="ecx-page-card ecx-page-panel surveys-panel">
                    <div className="surveys-filters">
                        <div className="surveys-filters__row">
                            <div className="surveys-filters__search input-group input-group-sm">
                                <span className="input-group-text bg-white border-end-0">
                                    <i className="bi bi-search text-muted" />
                                </span>
                                <input
                                    type="search"
                                    className="form-control form-control-sm border-start-0"
                                    placeholder="Filter templates…"
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    disabled={loading}
                                />
                            </div>
                        </div>
                    </div>

                    <div className="ecx-page-panel__header border-top-0">
                        <div className="surveys-toolbar">
                            <div className="surveys-toolbar__meta">
                                <h5 className="mb-0 fw-normal text-dark">Available templates</h5>
                                <span className="ecx-page-chip">
                                    <i className="bi bi-collection" />
                                    {filtered.length.toLocaleString()} shown
                                </span>
                            </div>
                        </div>
                    </div>

                    <div className="ecx-page-panel__body">
                        {loading ? (
                            <div className="text-muted py-5 text-center">Loading templates…</div>
                        ) : filtered.length === 0 ? (
                            <div className="ecx-page-empty">
                                <span className="ecx-page-empty__icon">
                                    <i className="bi bi-journal-x" />
                                </span>
                                <div className="fw-normal mb-1">No templates found</div>
                                <div className="small text-muted">Try a different search term.</div>
                            </div>
                        ) : (
                            <div className="surveys-template-grid">
                                {filtered.map((template) => (
                                    <div key={template.id} className="surveys-template-card">
                                        <div className="surveys-template-card__title">
                                            {template.title || 'Untitled template'}
                                        </div>
                                        <div className="surveys-template-card__meta">
                                            {template.category || 'Uncategorized'}
                                        </div>
                                        <p className="surveys-template-card__desc mb-0">
                                            {template.description || 'No description'}
                                        </p>
                                        <button
                                            type="button"
                                            className="btn btn-sm btn-primary rounded-pill align-self-start"
                                            disabled={installingId === template.id}
                                            onClick={() => handleInstall(template)}
                                        >
                                            {installingId === template.id ? 'Installing…' : 'Install as draft'}
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

export default SelectTemplate;
